import React from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination
} from '@mui/material';

const MonthlySalesReport = ({ data, onPageChange, onLimitChange, currentLimit }) => {
  // Generate month name from month number
  const getMonthName = (monthNum) => {
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return months[monthNum - 1] || 'Unknown';
  };

  // Safety check for data structure
  if (!data || !data.period || !data.summary) {
    return (
      <Box>
        <Typography variant="h5" gutterBottom>Monthly Sales Report</Typography>
        <Typography color="textSecondary">No data available for this report.</Typography>
      </Box>
    );
  }

  const monthName = data.period.monthName || getMonthName(data.period.month);

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Monthly Sales Report - {monthName} {data.period.year}
      </Typography>
      {data.location && (
        <Typography variant="subtitle1" color="textSecondary" gutterBottom>
          Location: {data.location}
        </Typography>
      )}
      {data.generatedAt && (
        <Typography variant="caption" color="textSecondary" gutterBottom display="block">
          Generated: {new Date(data.generatedAt).toLocaleString()}
        </Typography>
      )}
      
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Total Sales</Typography>
              <Typography variant="h5">${data.summary.totalSales.toFixed(2)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Total Invoices</Typography>
              <Typography variant="h5">{data.summary.invoiceCount}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Avg per Invoice</Typography>
              <Typography variant="h5">
                ${data.summary.invoiceCount > 0 
                  ? (data.summary.totalSales / data.summary.invoiceCount).toFixed(2)
                  : '0.00'
                }
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Total Discounts</Typography>
              <Typography variant="h5">${data.summary.totalDiscount.toFixed(2)}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Typography variant="h6" gutterBottom>Daily Sales Breakdown</Typography>
          {data.pagination && (
            <Typography variant="caption" color="textSecondary" gutterBottom display="block">
              Showing {data.pagination.startIndex} - {data.pagination.endIndex} of {data.pagination.totalCount} days
            </Typography>
          )}
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell align="right">Sales</TableCell>
                  <TableCell align="right">Invoices</TableCell>
                  <TableCell align="right">Tax</TableCell>
                  <TableCell align="right">Discount</TableCell>
                  <TableCell align="right">Refunds</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.dailyBreakdown && data.dailyBreakdown.map((day) => (
                  <TableRow key={day.date}>
                    <TableCell>{new Date(day.date + 'T00:00:00').toLocaleDateString()}</TableCell>
                    <TableCell align="right">${parseFloat(day.total_sales || 0).toFixed(2)}</TableCell>
                    <TableCell align="right">{parseInt(day.invoice_count || 0)}</TableCell>
                    <TableCell align="right">${parseFloat(day.total_tax || 0).toFixed(2)}</TableCell>
                    <TableCell align="right">${parseFloat(day.total_discount || 0).toFixed(2)}</TableCell>
                    <TableCell align="right" sx={{ color: parseFloat(day.total_refunds || 0) > 0 ? 'error.main' : 'inherit' }}>${parseFloat(day.total_refunds || 0).toFixed(2)}</TableCell>
                  </TableRow>
                ))}
                {(!data.dailyBreakdown || data.dailyBreakdown.length === 0) && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography color="textSecondary">No sales data found for this month</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          {/* Pagination Controls */}
          {data.pagination && data.pagination.totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Pagination 
                count={data.pagination.totalPages}
                page={data.pagination.currentPage}
                onChange={(event, page) => onPageChange && onPageChange(page)}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Typography variant="h6" gutterBottom>Sales by Category</Typography>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Category</TableCell>
                  <TableCell align="right">Items</TableCell>
                  <TableCell align="right">Value</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.categoryBreakdown && data.categoryBreakdown.map((category) => (
                  <TableRow key={category.category}>
                    <TableCell style={{ textTransform: 'capitalize' }}>{category.category}</TableCell>
                    <TableCell align="right">{parseInt(category.item_count || 0)}</TableCell>
                    <TableCell align="right">${parseFloat(category.total_value || 0).toFixed(2)}</TableCell>
                  </TableRow>
                ))}
                {(!data.categoryBreakdown || data.categoryBreakdown.length === 0) && (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      <Typography color="textSecondary">No category data found</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>
    </Box>
  );
};

export default MonthlySalesReport;
